import * as THREE from 'three';

const MAX_MOTES = 80;
const MAP_HALF = 88;
const MOTE_Y_MIN = 3;
const MOTE_Y_MAX = 32;

type Mote = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  bobPhase: number;
};

/**
 * Ambient golden/white pollen sparkles drifting across the map.
 */
export class FpsAmbientLife {
  private readonly motes: Mote[] = [];
  private readonly moteGeo = new THREE.PlaneGeometry(0.09, 0.09);
  private readonly baseMat = new THREE.MeshBasicMaterial({
    color: 0xfff6d8,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  constructor(private readonly scene: THREE.Scene) {
    while (this.motes.length < MAX_MOTES) {
      this.spawnMote(true);
    }
  }

  update(dt: number, camera?: THREE.Camera): void {
    while (this.motes.length < MAX_MOTES) {
      this.spawnMote(false);
    }

    for (const m of this.motes) {
      m.bobPhase += dt * (1.2 + Math.sin(m.bobPhase) * 0.28);
      m.mesh.position.addScaledVector(m.vel, dt);
      m.mesh.position.y += Math.sin(m.bobPhase) * dt * 0.28;

      const { x, y, z } = m.mesh.position;
      if (
        Math.abs(x) > MAP_HALF ||
        Math.abs(z) > MAP_HALF ||
        y < MOTE_Y_MIN - 2 ||
        y > MOTE_Y_MAX + 4
      ) {
        this.respawnMote(m);
        continue;
      }

      if (camera) {
        m.mesh.lookAt(camera.position);
      }

      const mat = m.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.34 + Math.sin(m.bobPhase * 1.6) * 0.16;
    }
  }

  dispose(): void {
    for (const m of this.motes) {
      this.scene.remove(m.mesh);
      if (m.mesh.material !== this.baseMat) {
        (m.mesh.material as THREE.Material).dispose();
      }
    }
    this.motes.length = 0;
    this.moteGeo.dispose();
    this.baseMat.dispose();
  }

  private spawnMote(_initial: boolean): void {
    const mesh = new THREE.Mesh(this.moteGeo, this.baseMat.clone());
    const x = (Math.random() - 0.5) * MAP_HALF * 2;
    const z = (Math.random() - 0.5) * MAP_HALF * 2;
    const y = MOTE_Y_MIN + Math.random() * (MOTE_Y_MAX - MOTE_Y_MIN);
    mesh.position.set(x, y, z);
    const mat = mesh.material as THREE.MeshBasicMaterial;
    const golden = Math.random() < 0.62;
    mat.color.setHSL(
      golden ? 0.11 + Math.random() * 0.05 : 0.58,
      golden ? 0.35 + Math.random() * 0.25 : 0.08,
      golden ? 0.78 + Math.random() * 0.14 : 0.92 + Math.random() * 0.06,
    );
    mat.opacity = 0.38 + Math.random() * 0.22;
    this.scene.add(mesh);
    this.motes.push({
      mesh,
      vel: new THREE.Vector3(
        (Math.random() - 0.5) * 1.6,
        0.04 + Math.random() * 0.14,
        (Math.random() - 0.5) * 1.6,
      ),
      bobPhase: Math.random() * Math.PI * 2,
    });
  }

  private respawnMote(m: Mote): void {
    const edge = Math.floor(Math.random() * 4);
    const y = MOTE_Y_MIN + Math.random() * (MOTE_Y_MAX - MOTE_Y_MIN);
    let x = 0;
    let z = 0;
    const span = MAP_HALF * 0.92;
    if (edge === 0) {
      x = -span;
      z = (Math.random() - 0.5) * span * 2;
    } else if (edge === 1) {
      x = span;
      z = (Math.random() - 0.5) * span * 2;
    } else if (edge === 2) {
      z = -span;
      x = (Math.random() - 0.5) * span * 2;
    } else {
      z = span;
      x = (Math.random() - 0.5) * span * 2;
    }
    m.mesh.position.set(x, y, z);
    m.vel.set(
      (Math.random() - 0.5) * 1.8,
      0.03 + Math.random() * 0.12,
      (Math.random() - 0.5) * 1.8,
    );
    m.bobPhase = Math.random() * Math.PI * 2;
  }
}
